"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ArrowDownToLine, MoveRight, PackageCheck, CircleAlert as AlertCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const checkpoints = [{
  number: "01",
  icon: MapPin,
  title: "Site Access",
  description: "Consider gates, driveways, loading areas and other access points at the destination."
}, {
  number: "02",
  icon: ArrowDownToLine,
  title: "Unloading",
  description: "Confirm how the shipment will be unloaded based on the actual delivery arrangement."
}, {
  number: "03",
  icon: MoveRight,
  title: "Equipment Access",
  description: "Consider how the packed equipment will move from the delivery point to its final setup location."
}, {
  number: "04",
  icon: PackageCheck,
  title: "Storage Before Setup",
  description: "If the bull will not be assembled immediately, plan a suitable location for the delivered components."
}];
export default function Delivery() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="deliverypreparation" data-nav="light" className="bg-background text-foreground py-20 md:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mx-auto text-center mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 border border-border bg-muted/60 px-3 py-1 text-xs font-mono tracking-widest uppercase text-muted-foreground mb-4">
            <span>Logistics Notice</span>
            <span className="text-border">|</span>
            <span>Commercial Freight</span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold uppercase tracking-tight text-foreground">
            Plan the Delivery Location Before Ordering
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A successful delivery starts with understanding the destination. Large equipment may require more room and access than a standard courier package.
          </p>
        </div>

        {/* 4-Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {checkpoints.map((card, i) => {
          const IconComponent = card.icon;
          return <motion.div key={card.number} data-index={i} variants={cardVariants} className="group relative bg-card border border-border p-6 flex flex-col justify-between transition-[border-color] duration-200 hover:border-foreground">
                {/* Header within card with index datum */}
                <div>
                  <div className="flex items-center justify-between pb-4 mb-5 border-b border-border">
                    <span className="font-mono text-xs font-semibold text-muted-foreground tracking-wider">
                      CHECKPOINT // {card.number}
                    </span>
                    <div className="size-8 border border-border flex items-center justify-center bg-muted/30 text-foreground group-hover:border-foreground transition-colors duration-150">
                      <IconComponent className="size-4" />
                    </div>
                  </div>

                  <h3 className="text-xl font-serif font-bold uppercase tracking-wide text-card-foreground mb-3">
                    {card.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {card.description}
                  </p>
                </div>

                {/* Bottom mechanical indicator line */}
                <div className="mt-6 pt-3 border-t border-border/50 flex items-center justify-between text-[11px] font-mono text-muted-foreground">
                  <span>STATUS</span>
                  <span className="text-foreground font-medium">PRE-TRANSIT</span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Metal-Plate Callout Banner */}
        <div className="mt-10 sm:mt-14 border border-border bg-muted/40 p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4 max-w-3xl">
            <div className="size-9 shrink-0 border border-border bg-card flex items-center justify-center text-primary mt-0.5">
              <AlertCircle className="size-5" />
            </div>
            <div>
              <p className="text-sm sm:text-base text-foreground font-medium leading-normal">
                If destination access could affect delivery, mention it during the enquiry rather than waiting until the shipment is already in transit.
              </p>
              <p className="text-xs font-mono text-muted-foreground mt-1 tracking-tight">
                Freight carriers cannot alter routing or offloading equipment once dispatched.
              </p>
            </div>
          </div>

          <div className="shrink-0 w-full md:w-auto">
            <Button asChild variant="outline" className="w-full md:w-auto font-mono text-xs uppercase tracking-wider h-10 px-5 border-border hover:border-foreground">
              <Link to="/ContactPage">
                Discuss Access
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}